import { useState } from 'react';
import Breadcrumb from '../components/Breadcrumb';
import SectionEyebrow from '../components/SectionEyebrow';
import { lotTiers } from '../data/lots';

type Timeline = '30d' | '3m' | '6m' | 'exploring';
type Usage = 'residence' | 'rental' | 'investment';

export default function Reserve() {
  const [selectedTier, setSelectedTier] = useState<string>('garden');
  const [timeline, setTimeline] = useState<Timeline>('3m');
  const [usages, setUsages] = useState<Usage[]>([]);
  const [agreed, setAgreed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Controlled field state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName]   = useState('');
  const [email, setEmail]         = useState('');
  const [phone, setPhone]         = useState('');
  const [location, setLocation]   = useState('');
  const [source, setSource]       = useState('');
  const [notes, setNotes]         = useState('');

  function toggleUsage(u: Usage) {
    setUsages((prev) =>
      prev.includes(u) ? prev.filter((x) => x !== u) : [...prev, u]
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!agreed) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/reserve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName, lastName, email, phone, location,
          source, timeline, usages, notes,
          tier: selectedTier,
        }),
      });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please email us directly at hello@costanativa.co.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      {/* HERO */}
      <section className="pt-48 pb-20 bg-forest-900 text-cream relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 grain" aria-hidden />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
          <div className="text-cream/60 mb-10">
            <Breadcrumb current="Reserve a Lot" />
          </div>

          <div className="max-w-4xl">
            <div className="w-20 h-20 mb-10 rounded-full bg-sand-300/20 border border-sand-300/40 flex items-center justify-center">
              <img src="/favicon2.svg" alt="Nativa" className="w-12 h-12 object-contain" />
            </div>
            <SectionEyebrow>
              <span className="text-sand-300">Phase 1 · Open now</span>
            </SectionEyebrow>
            <h1 className="mt-6 font-display text-5xl lg:text-7xl leading-[1]">
              Hold your spot. Visit the land.{' '}
              <span className="italic text-sand-300">
                Decide with your feet on the ground.
              </span>
            </h1>
            <p className="mt-8 max-w-2xl text-cream/80 text-lg leading-relaxed">
              A refundable $5,000 deposit reserves a tier slot in the Phase 1
              queue. No specific lot committed yet, you choose that when you
              visit.
            </p>
            <div className="mt-8 flex flex-wrap gap-6 text-sm">
              <Pill>Fully refundable</Pill>
              <Pill>48-hour response</Pill>
              <Pill>No high-pressure tactics</Pill>
            </div>
          </div>
        </div>
      </section>

      {/* TIER PICKER */}
      <section className="py-20 bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-12">
            <SectionEyebrow>How this works</SectionEyebrow>
            <h2 className="mt-6 font-display text-4xl lg:text-5xl text-forest-900 leading-tight">
              Choose a tier. Tell us about{' '}
              <span className="italic text-clay-500">you</span>. Visit the
              land. Decide.
            </h2>
            <p className="mt-6 text-forest-900/70 leading-relaxed">
              This is the start of a conversation, not a commitment. We will
              respond within 48 hours, walk you through Phase 1 inventory in
              your tier, and schedule a visit weekend on the property.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {lotTiers.map((t) => (
              <button
                key={t.id}
                onClick={() => setSelectedTier(t.id)}
                className={`p-6 lg:p-8 text-left border-2 transition-all ${
                  selectedTier === t.id
                    ? 'border-clay-500 bg-clay-500/5 shadow-lg'
                    : 'border-forest-900/10 hover:border-forest-900/30'
                }`}
              >
                <div className="eyebrow text-clay-600">
                  {t.name.replace('The ', '').toUpperCase()}
                </div>
                <div className="mt-3 text-xs uppercase tracking-widest-2 text-forest-700/70">
                  {t.size}
                </div>
                <div className="mt-6 font-display text-3xl text-forest-900">
                  {t.startingPrice}
                </div>
                <div className="text-xs uppercase tracking-wider text-forest-900/50">
                  Starting from
                </div>
                <p className="mt-5 text-sm text-forest-900/70 leading-relaxed">
                  {t.description.split('. ')[0]}.
                </p>
                {selectedTier === t.id && (
                  <div className="mt-5 text-xs uppercase tracking-widest-2 text-clay-600 font-medium">
                    ✓ Selected
                  </div>
                )}
              </button>
            ))}
          </div>

          <button
            onClick={() => setSelectedTier('guidance')}
            className={`mt-4 text-sm ${
              selectedTier === 'guidance'
                ? 'text-clay-600 font-medium'
                : 'text-forest-900/60 hover:text-forest-900'
            }`}
          >
            Not sure yet? I would like guidance on the right tier →
          </button>

          {selectedTier === 'guidance' && (
            <div className="mt-6 p-6 border border-clay-500/30 bg-clay-50">
              <p className="text-forest-900 font-medium">
                No problem — we'll find the right fit for you.
              </p>
              <p className="mt-2 text-sm text-forest-900/70 leading-relaxed">
                Fill out the form below. Tell us about how you'd use the property,
                your timeline, and budget range. We'll come back within 48 hours
                with a personal recommendation and Phase 1 inventory in the tiers
                that match.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* FORM */}
      <section className="py-20 bg-forest-50">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <SectionEyebrow>
            {selectedTier === 'guidance' ? 'Find your fit' : 'Reserve your tier slot'}
          </SectionEyebrow>
          <h2 className="mt-6 font-display text-4xl lg:text-5xl text-forest-900 leading-tight">
            {selectedTier === 'guidance' ? (
              <>We will match you to the <span className="italic text-clay-500">right tier</span>.</>
            ) : (
              <>Tell us a little about <span className="italic text-clay-500">you</span>.</>
            )}
          </h2>
          <p className="mt-6 text-forest-900/70 leading-relaxed">
            The more we know, the better we can tailor your visit and inventory
            recommendations. Nothing here is a commitment, and we never share
            your information.
          </p>

          {submitted ? (
            <div className="mt-12 p-10 bg-cream border border-forest-900/10 text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-forest-700 flex items-center justify-center text-cream text-3xl">
                ✓
              </div>
              <h3 className="font-display text-3xl text-forest-900">
                Thank you. We have your details.
              </h3>
              <p className="mt-4 text-forest-900/70 leading-relaxed">
                A real human will respond within 48 hours with Phase 1
                inventory in your tier and visit weekend options. Keep an eye
                on your inbox.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-12 space-y-8">
              {/* Name */}
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="First name" required>
                  <input
                    type="text"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full bg-cream border border-forest-900/15 px-4 py-3 focus:border-clay-500 focus:outline-none transition-colors"
                  />
                </Field>
                <Field label="Last name" required>
                  <input
                    type="text"
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full bg-cream border border-forest-900/15 px-4 py-3 focus:border-clay-500 focus:outline-none transition-colors"
                  />
                </Field>
              </div>

              <Field label="Email" required>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-cream border border-forest-900/15 px-4 py-3 focus:border-clay-500 focus:outline-none transition-colors"
                />
              </Field>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Phone (optional)">
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full bg-cream border border-forest-900/15 px-4 py-3 focus:border-clay-500 focus:outline-none transition-colors"
                  />
                </Field>
                <Field label="Where are you based?">
                  <input
                    type="text"
                    placeholder="City, country"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="w-full bg-cream border border-forest-900/15 px-4 py-3 focus:border-clay-500 focus:outline-none transition-colors"
                  />
                </Field>
              </div>

              <Field label="How did you hear about us?">
                <select
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                  className="w-full bg-cream border border-forest-900/15 px-4 py-3 focus:border-clay-500 focus:outline-none transition-colors"
                >
                  <option value="">Select one...</option>
                  <option value="Friend or referral">Friend or referral</option>
                  <option value="Web search">Web search</option>
                  <option value="Instagram">Instagram</option>
                  <option value="Real estate broker">Real estate broker</option>
                  <option value="Press / article">Press / article</option>
                  <option value="Other">Other</option>
                </select>
              </Field>

              {/* Timeline */}
              <Field label="What is your timeline?">
                <div className="space-y-3 mt-2">
                  {(
                    [
                      ['30d', 'Ready to reserve in the next 30 days'],
                      ['3m', 'Within the next 3 months'],
                      ['6m', 'Within 6 months'],
                      ['exploring', 'Just exploring, no rush'],
                    ] as [Timeline, string][]
                  ).map(([val, label]) => (
                    <label
                      key={val}
                      className="flex items-center gap-3 cursor-pointer group"
                    >
                      <input
                        type="radio"
                        name="timeline"
                        value={val}
                        checked={timeline === val}
                        onChange={() => setTimeline(val)}
                        className="w-4 h-4 accent-clay-500"
                      />
                      <span className="text-forest-900 group-hover:text-clay-600 transition-colors">
                        {label}
                      </span>
                    </label>
                  ))}
                </div>
              </Field>

              {/* Usage */}
              <Field label="How do you imagine using your lot? (select any)">
                <div className="space-y-3 mt-2">
                  {(
                    [
                      ['residence', 'Personal residence / second home'],
                      ['rental', 'Rental income (Airbnb / boutique)'],
                      ['investment', 'Long-term investment / hold'],
                    ] as [Usage, string][]
                  ).map(([val, label]) => (
                    <label
                      key={val}
                      className="flex items-center gap-3 cursor-pointer group"
                    >
                      <input
                        type="checkbox"
                        checked={usages.includes(val)}
                        onChange={() => toggleUsage(val)}
                        className="w-4 h-4 accent-clay-500"
                      />
                      <span className="text-forest-900 group-hover:text-clay-600 transition-colors">
                        {label}
                      </span>
                    </label>
                  ))}
                </div>
              </Field>

              <Field label="Anything else we should know? (optional)">
                <textarea
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full bg-cream border border-forest-900/15 px-4 py-3 focus:border-clay-500 focus:outline-none transition-colors resize-none"
                  placeholder="Family situation, specific lot preferences, questions you want answered on a visit..."
                />
              </Field>

              {/* Agree */}
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="w-4 h-4 mt-1 accent-clay-500 flex-shrink-0"
                />
                <span className="text-sm text-forest-900/80 leading-relaxed">
                  I understand the $5,000 reservation deposit is fully
                  refundable until I sign a purchase agreement on a specific
                  lot. I agree to the{' '}
                  <a href="mailto:hello@costanativa.co" className="underline underline-offset-4 hover:text-clay-600">
                    privacy policy
                  </a>
                  .
                </span>
              </label>

              {error && (
                <p className="text-sm text-clay-600 border border-clay-500/30 bg-clay-50 px-4 py-3">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={!agreed || loading}
                className={`w-full py-5 font-medium tracking-wide transition-colors ${
                  agreed && !loading
                    ? 'bg-forest-700 text-cream hover:bg-forest-900'
                    : 'bg-forest-900/20 text-forest-900/50 cursor-not-allowed'
                }`}
              >
                {loading ? 'Sending…' : 'Reserve My Tier Slot →'}
              </button>

              <p className="text-center text-sm text-forest-900/60">
                We will respond within 48 hours with next steps and visit
                options.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* WHAT HAPPENS NEXT */}
      <section className="py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-16">
            <SectionEyebrow>What happens next</SectionEyebrow>
            <h2 className="mt-6 font-display text-4xl lg:text-5xl text-forest-900 leading-tight">
              From <span className="italic text-clay-500">this form</span> to
              keys, in clear steps.
            </h2>
            <p className="mt-6 text-forest-900/70 leading-relaxed">
              No black box. No high-pressure tactics. Here is exactly what to
              expect.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <NextStep
              n="01"
              when="Within 48 hours"
              title="We respond personally."
              body="A real human emails you back, no auto-responder. We confirm your tier interest, send Phase 1 inventory in your range, and propose visit weekend dates."
            />
            <NextStep
              n="02"
              when="Within 7 days"
              title="You confirm reservation."
              body="Once you are ready to hold the slot, you wire the $5,000 refundable deposit. We send a queue-position confirmation and a welcome packet."
            />
            <NextStep
              n="03"
              when="Within 90 days"
              title="You visit the land."
              body="Three days, two nights on-property. Transport from Limón airport, accommodation, and full guided exploration. Walk the river. Walk available lots."
            />
            <NextStep
              n="04"
              when="When you are ready"
              title="You close on a specific lot."
              body="Costa Rica attorney handles title, SETENA filings, and corporation setup if needed. Closing typically 30 to 60 days after lot selection."
            />
          </div>
        </div>
      </section>

      {/* CONTACT DIRECTLY */}
      <section className="py-20 bg-forest-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <SectionEyebrow>Or skip the form</SectionEyebrow>
              <h3 className="mt-4 font-display text-3xl text-forest-900">
                Reach us directly.
              </h3>
              <p className="mt-4 text-forest-900/70 leading-relaxed">
                Sometimes a conversation is faster than a form. We are real
                people. We answer.
              </p>
              <dl className="mt-8 space-y-5">
                <Contact label="Email" value="hello@costanativa.co" href="mailto:hello@costanativa.co"/>
                <Contact label="Schedule a call" value="Book 30 min →" href="https://calendar.app.google/5TmsQM5yNvGoWraz6" />
                <Contact
                  label="On the ground"
                  value="San Clemente, Limón"                />
              </dl>
            </div>
            <div className="bg-cream p-8 border border-forest-900/10">
              <h3 className="font-display text-2xl text-forest-900">
                Your information stays with us.
              </h3>
              <p className="mt-4 text-forest-900/70 text-sm leading-relaxed">
                We do not sell, share, or rent your data. Your details are used
                solely to respond to your inquiry and (if you opt in) to send
                periodic project updates. You can request deletion at any
                time.
              </p>
              <div className="mt-6 flex gap-4 text-sm">
                <a href="#" className="text-clay-600 underline underline-offset-4 hover:text-clay-700">
                  Privacy policy
                </a>
                <a href="#" className="text-clay-600 underline underline-offset-4 hover:text-clay-700">
                  Terms
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Pill({ children }: { children: React.ReactNode }) {
  return (
    <div className="inline-flex items-center gap-2 px-4 py-2 border border-cream/20 text-cream/80">
      <span className="w-1.5 h-1.5 rounded-full bg-sand-300" />
      {children}
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-forest-900 mb-2">
        {label}
        {required && <span className="text-clay-500 ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}

function NextStep({
  n,
  when,
  title,
  body,
}: {
  n: string;
  when: string;
  title: string;
  body: string;
}) {
  return (
    <div className="border-t-2 border-clay-500 pt-6">
      <div className="font-display text-3xl text-clay-500">{n}</div>
      <div className="mt-2 eyebrow text-forest-700/70">{when}</div>
      <h3 className="mt-3 font-display text-xl text-forest-900">{title}</h3>
      <p className="mt-3 text-sm text-forest-900/70 leading-relaxed">{body}</p>
    </div>
  );
}

function Contact({
  label,
  value,
  href,
}: {
  label: string;
  value: string;
  href?: string;
}) {
  return (
    <div>
      <dt className="eyebrow text-forest-700/60">{label}</dt>
      <dd className="mt-1 font-display text-2xl text-forest-900">
        {href ? (
          <a
            href={href}
            target={href.startsWith('http') ? '_blank' : undefined}
            rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
            className="hover:text-clay-600 transition-colors underline decoration-clay-500/30 underline-offset-4"
          >
            {value}
          </a>
        ) : (
          value
        )}
      </dd>
    </div>
  );
}
